import { useQuery, keepPreviousData } from '@tanstack/react-query'

import { trackEvent } from '@classic/Foundation/Analytics/GoogleTagManagerService'
import { keys as analyticsKeys } from '@classic/Foundation/Analytics/AnalyticsDataLayer'
import { get } from '@classic/Foundation/Services/ApiService'
import { queryKeys } from '@core/Data/ReactQuery/constants'
import { RaceStatus } from '@mobi/api-types'
import { NextRace } from '@core/Data/Racing/nextRaces'
import { store } from '@core/Store'
import { useAppSelector } from '@core/Store/hooks'
import { getSkyRaceLimit } from '../Store/selectors'
import { setSkyRaceLimit } from '../Store'

const MaxRefetchInterval = 120_000

export const SkyPageLimit = 50

export interface NextSkyRace extends NextRace {
  RaceStatus: RaceStatus
  IsoCountryCode: string
  SimplePlacings: string
}

export const useNextSkyRaces = (
  opts: { isEnabled?: boolean; refetchOnMount?: boolean } = {
    isEnabled: true,
    refetchOnMount: true,
  }
) => {
  const limit = useAppSelector(getSkyRaceLimit)

  const fetchSkyRaces = async () => {
    return await get<{ nextRaces: NextSkyRace[] }>({
      url: `/webapi/race/GetNextSkyRaces${limit ? `?limit=${limit}` : ''}`,
    })
  }

  const { isLoading, isError, data, refetch } = useQuery<{
    nextRaces: NextSkyRace[]
  }>({
    queryKey: [queryKeys.nextSkyRaces, limit],
    queryFn: fetchSkyRaces,
    enabled: opts.isEnabled,
    refetchOnWindowFocus: true,
    refetchOnMount: opts.refetchOnMount,
    refetchIntervalInBackground: false,
    refetchInterval: MaxRefetchInterval,
    placeholderData: keepPreviousData,
  })

  const fetchMoreRaces = () => {
    store.dispatch(setSkyRaceLimit(limit + SkyPageLimit))
    trackEvent(analyticsKeys.moreSkyRacesLoaded, {
      clickCount: limit / SkyPageLimit,
    })
  }

  const resetLimit = () => {
    store.dispatch(setSkyRaceLimit(SkyPageLimit))
  }

  const nextRaces = data?.nextRaces || []

  return {
    nextRaces,
    isLoading,
    isError,
    isMoreToFetch: nextRaces.length === limit,
    refetch: refetch,
    fetchMoreRaces,
    resetLimit,
  }
}
